// Clasificacion de las compras (Anexo 3 - Detalle de compras, columnas Q, R, S, T del manual F-07 v14).
// Obligatoria desde febrero 2024; antes de esa fecha el manual pide 0 en las 4 columnas.
// Los codigos 8 (operacion informada en otro anexo) y 9 (instituciones publicas / no deducible) aplican a las 4 columnas.
// Lo comparten la pantalla "Validar datos" (carga de libro nuevo) y "Ver libro" (libros ya generados).
export type CampoCodigo = 'tipoOperacion' | 'clasificacion' | 'sector' | 'tipoClasificacion';
export type Codigos = Record<CampoCodigo, number>;

const OTROS_CODIGOS = [{ valor: 8, texto: '8 - Informada en otro anexo' }, { valor: 9, texto: '9 - No deducible / institución pública' }];

export const OPCIONES_CODIGOS: Record<CampoCodigo, { etiqueta: string; opciones: Array<{ valor: number; texto: string }> }> = {
  tipoOperacion: { etiqueta: 'Tipo de operación', opciones: [
    { valor: 1, texto: '1 - Gravada' }, { valor: 2, texto: '2 - No gravada' }, { valor: 3, texto: '3 - Excluido o no constituye renta' }, { valor: 4, texto: '4 - Mixta' }, ...OTROS_CODIGOS ] },
  clasificacion: { etiqueta: 'Clasificación', opciones: [
    { valor: 1, texto: '1 - Costo' }, { valor: 2, texto: '2 - Gasto' }, ...OTROS_CODIGOS ] },
  sector: { etiqueta: 'Sector', opciones: [
    { valor: 1, texto: '1 - Industria' }, { valor: 2, texto: '2 - Comercio' }, { valor: 3, texto: '3 - Agropecuaria' }, { valor: 4, texto: '4 - Servicios, profesiones, artes y oficios' }, ...OTROS_CODIGOS ] },
  tipoClasificacion: { etiqueta: 'Tipo de costo / gasto', opciones: [
    { valor: 1, texto: '1 - Gastos de venta sin donación' }, { valor: 2, texto: '2 - Gastos de administración sin donación' }, { valor: 3, texto: '3 - Gastos financieros sin donación' },
    { valor: 4, texto: '4 - Costo artículos producidos/comprados importaciones/internaciones' }, { valor: 5, texto: '5 - Costo artículos producidos/comprados interno' },
    { valor: 6, texto: '6 - Costos indirectos de fabricación' }, { valor: 7, texto: '7 - Mano de obra' }, ...OTROS_CODIGOS ] },
};

export const CAMPOS_CODIGO = Object.keys(OPCIONES_CODIGOS) as Array<CampoCodigo>;
export const CODIGOS_VACIOS: Codigos = { tipoOperacion: 0, clasificacion: 0, sector: 0, tipoClasificacion: 0 };

// Pregunta corta que se muestra bajo cada selector para que se entienda que se esta eligiendo
export const PREGUNTAS_CODIGO: Record<CampoCodigo, string> = {
  tipoOperacion: '¿Cómo se grava la compra?',
  clasificacion: '¿Es un costo o un gasto?',
  sector: '¿A qué sector pertenece?',
  tipoClasificacion: '¿En qué se usó la compra?',
};

// Nombre de cada campo en el detalle que devuelve el servidor (columnas de DetalleLibroCompras)
export const CAMPO_DETALLE: Record<CampoCodigo, string> = {
  tipoOperacion: 'tipooperacion',
  clasificacion: 'clasificacion',
  sector: 'sector',
  tipoClasificacion: 'tipoclasificacion',
};

export const esCodigoValido = (campo: CampoCodigo, valor: any) => OPCIONES_CODIGOS[campo].opciones.some((op) => op.valor === Number(valor));

// El periodo llega como AAAA-MM (carga nueva), MM/AAAA o, en libros viejos, DD-MM-AA. Si no se puede interpretar se exige
// (lo seguro: pedir la clasificacion). Antes de febrero 2024 las 4 columnas van en 0.
export const periodoExigeClasificacion = (periodo?: string): boolean => {
  const texto = (periodo ?? '').trim();
  let anio = NaN;
  let mes = NaN;
  let m = texto.match(/^(\d{4})-(\d{1,2})/);
  if (m) { anio = Number(m[1]); mes = Number(m[2]); }
  else if ((m = texto.match(/^(\d{1,2})\/(\d{4})$/))) { mes = Number(m[1]); anio = Number(m[2]); }
  else if ((m = texto.match(/^(\d{1,2})-(\d{1,2})-(\d{2})$/))) { mes = Number(m[2]); anio = 2000 + Number(m[3]); }
  if (Number.isNaN(anio) || Number.isNaN(mes)) return true;
  return anio * 12 + mes >= 2024 * 12 + 2;
};

export interface ResultadoAplicacion {
  // Solo los campos que se aplicaron, por clave de documento: se mezcla con lo que ya haya editado el usuario
  parches: Record<string, Partial<Codigos>>;
  // A cuantas compras cambio algo
  compras: number;
  // Cuantos datos se habian elegido en el panel (0 = no eligio nada)
  elegidos: number;
}

// Calcula a que compras y campos se aplica lo elegido en el panel. Funcion pura: no toca ningun estado.
export const calcularAplicacion = (
  items: Array<any>,
  claveItem: (item: any) => string,
  valorActual: (item: any, campo: CampoCodigo) => number,
  masivo: Codigos,
  soloSinClasificar: boolean,
): ResultadoAplicacion => {
  const camposElegidos = CAMPOS_CODIGO.filter((campo) => masivo[campo] > 0);
  const parches: Record<string, Partial<Codigos>> = {};
  let compras = 0;
  if (camposElegidos.length === 0) return { parches, compras, elegidos: 0 };
  items.forEach((item) => {
    let cambio = false;
    camposElegidos.forEach((campo) => {
      const actual = valorActual(item, campo);
      if (soloSinClasificar && esCodigoValido(campo, actual)) return;
      parches[claveItem(item)] = { ...parches[claveItem(item)], [campo]: masivo[campo] };
      if (actual !== masivo[campo]) cambio = true;
    });
    if (cambio) compras++;
  });
  return { parches, compras, elegidos: camposElegidos.length };
};

export const mensajeAplicacion = (compras: number): string =>
  compras === 0
    ? 'Las compras ya tenían esa clasificación: no se cambió nada.'
    : `Se aplicó a ${compras} ${compras === 1 ? 'compra' : 'compras'}.`;
